// src/hooks/common/useTableActions.js
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const useTableActions = ({
    basePath,
    deleteSingle,
    deleteAll,
    restoreSingle,
    restoreAll,
    forceDeleteSingle,
    forceDeleteAll,
    duplicateSingle,
    isArchive = false,
}) => {
    const navigate = useNavigate();

    // State Management
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [search, setSearch] = useState("");
    const [order, setOrder] = useState("asc");
    const [selectedIds, setSelectedIds] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    // Sorting
    const handleRequestSort = useCallback(() => {
        setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    }, []);

    // Row Selection
    const handleRowCheckboxClick = useCallback((event, id) => {
        event.stopPropagation();
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAllClick = useCallback((event, items) => {
        if (event.target.checked) {
            const allIds = items.map((item) => item.id);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    }, []);

    // ✅ تعريف handleMenuClose أولاً
    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setSelectedItem(null);
    }, []);

    // Menu Actions
    const handleMenuOpen = useCallback((event, item) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedItem(item);
    }, []);

    // ✨ NEW: View Action
    const handleView = useCallback(() => {
        if (!selectedItem) return;
        navigate(`${basePath}/${selectedItem.id}`);
        handleMenuClose();
    }, [selectedItem, basePath, navigate, handleMenuClose]);

    // Edit Action
    const handleEdit = useCallback(() => {
        if (!selectedItem) return;
        navigate(`${basePath}/${selectedItem.id}/edit`);
        handleMenuClose();
    }, [selectedItem, basePath, navigate, handleMenuClose]);

    // Delete Action (soft-delete if in normal mode, force-delete if in archive mode)
    const handleMenuDelete = useCallback(() => {
        if (!selectedItem) return;

        if (isArchive) {
            forceDeleteSingle?.(selectedItem.id, {
                onSuccess: () => {
                    setSelectedIds([]);
                },
            });
        } else {
            deleteSingle?.(selectedItem.id, {
                onSuccess: () => {
                    setSelectedIds([]);
                },
            });
        }

        handleMenuClose();
    }, [selectedItem, isArchive, forceDeleteSingle, deleteSingle, handleMenuClose]);

    // Restore Action
    const handleMenuRestore = useCallback(() => {
        if (!selectedItem) return;
        restoreSingle?.(selectedItem.id, {
            onSuccess: () => {
                setSelectedIds([]);
            },
        });
        handleMenuClose();
    }, [selectedItem, restoreSingle, handleMenuClose]);

    // Duplicate Action
    const handleMenuDuplicate = useCallback(() => {
        if (!selectedItem) return;
        duplicateSingle?.(selectedItem.id, {
            onSuccess: () => {
                setSelectedIds([]);
            },
        });
        handleMenuClose();
    }, [selectedItem, duplicateSingle, handleMenuClose]);

    // Bulk Delete
    const handleBulkDelete = useCallback(() => {
        if (!selectedIds.length) return;

        if (isArchive) {
            forceDeleteAll?.({ items: selectedIds });
        } else {
            deleteAll?.({ items: selectedIds });
        }

        setSelectedIds([]);
    }, [selectedIds, isArchive, forceDeleteAll, deleteAll]);

    // Bulk Restore
    const handleBulkRestore = useCallback(() => {
        if (!selectedIds.length) return;

        restoreAll?.({ items: selectedIds });

        setSelectedIds([]);
    }, [selectedIds, restoreAll]);

    // Pagination
    const handlePageChange = useCallback((value) => {
        setPage(value);
    }, []);

    const handlePerPageChange = useCallback((value) => {
        setPerPage(value);
        setPage(1);
    }, []);

    // Clear Selection
    const clearSelection = useCallback(() => {
        setSelectedIds([]);
    }, []);

    return {
        // State
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        selectedItem,

        // State Setters
        setPage,
        setPerPage,
        setSearch,
        setOrder,
        setSelectedIds,

        // Handlers
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,        // ✨ NEW
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleMenuDuplicate,
        handleBulkDelete,
        handleBulkRestore, // ✨ NEW
        handlePageChange,
        handlePerPageChange,
        clearSelection,
    };
};

export default useTableActions;
